import { logger } from '@/utils/logger';
import { useCallback, useEffect, useState } from 'react';
import type { AppStoreFragment } from '@/utils/dato/generated';
import { AppStoreButtons as Buttons } from '@/components/AppStoreButtons';
import {
  STORAGE_KEY_FIRST_TOUCH,
  getCampaignContext,
  getAnalyticsIds,
  pushToDataLayer,
  getGaClientId,
} from '@/utils/gtm';

const AppStoreButtons = ({ appsflyerSmartlinkOptions, additionalUrlParameters, id }: AppStoreFragment) => {
  const [url, setUrl] = useState('https://zopa.onelink.me/cYa5?af_ss_ver=2_9_0&af_js_web=true&pid=galleria_organic');

  useEffect(() => {
    if (!window.AF_SMART_SCRIPT) {
      return;
    }

    const appsFlyerOptions = appsflyerSmartlinkOptions as GenerateOneLinkURLArguments;

    if (!appsFlyerOptions?.oneLinkURL) {
      logger.error({
        message: 'Provided Appsflyer options object is invalid',
        page: window.location.href,
        id: 'apps-store-buttons',
        appsFlyerOptions,
        moduleId: id,
      });
      return;
    }

    // https://dev.appsflyer.com/hc/docs/dl_smart_script_v2#arguments
    const link = window.AF_SMART_SCRIPT.generateOneLinkURL(appsflyerSmartlinkOptions as GenerateOneLinkURLArguments);

    if (link) {
      const url = new URL(link.clickURL);

      const urlParams = new URLSearchParams(url.search);

      additionalUrlParameters.forEach((param) => {
        urlParams.append(param.key, param.value || '');
      });

      const { acquisition_id: acquisitionId, zopa_session_id: sessionId } = getAnalyticsIds();
      const gaClientId = getGaClientId();

      if (acquisitionId) {
        urlParams.set('af_sub1', acquisitionId);
      }
      if (sessionId) {
        urlParams.set('af_sub3', sessionId);
      }
      // af_sub5 carries the GA client id so the app install can be joined to the web session in GA4.
      if (gaClientId && !urlParams.has('af_sub5')) {
        urlParams.set('af_sub5', gaClientId);
      }

      url.search = `?${urlParams.toString()}`;

      setUrl(url.toString());
    }
  }, [appsflyerSmartlinkOptions, additionalUrlParameters, id]);

  const handleStoreClick = useCallback((href: string) => {
    const firstTouch = JSON.parse(localStorage.getItem(STORAGE_KEY_FIRST_TOUCH) ?? '{}') as Partial<
      ReturnType<typeof getCampaignContext>
    >;
    const campaign = getCampaignContext(new URLSearchParams(window.location.search));

    pushToDataLayer({
      event_timestamp: new Date().toISOString(),
      event: 'app_store_click',
      ...getAnalyticsIds(),
      campaign_id: campaign.campaign_id || firstTouch.campaign_id || '',
      campaign: campaign.campaign || firstTouch.campaign || '',
      source: campaign.source || firstTouch.source || '',
      medium: campaign.medium || firstTouch.medium || '',
      ad_id: campaign.ad_id || firstTouch.ad_id || '',
      onelink_destination: href,
    });
  }, []);

  return <Buttons onStoreClick={handleStoreClick} googleStoreLink={url} appleStoreLink={url} />;
};

export default AppStoreButtons;
